import type { ReactElement } from 'react';
import { PinEntryScreen } from '@/presentation/components/PinEntryScreen';
import { ConfigLoadingScreen } from '@/presentation/components/ConfigLoadingScreen';
import { SessionExpiredScreen } from '@/presentation/components/SessionExpiredScreen';
import { FakeLeadCaptureForm } from '@/presentation/components/FakeLeadCaptureForm';
import { LeadCaptureSuccess } from '@/presentation/components/LeadCaptureSuccess';
import { OnlineStatusIndicator } from '@/presentation/components/OnlineStatusIndicator';
import { EndSessionGesture } from '@/presentation/components/EndSessionGesture';
import { QueueAlertToast } from '@/presentation/components/QueueAlertToast';
import { QueueFullModal } from '@/presentation/components/QueueFullModal';
import { PluginRegistryResolvingScreen } from '@/presentation/components/PluginRegistryResolvingScreen';
import { PluginNotConfiguredScreen } from '@/presentation/components/PluginNotConfiguredScreen';
import { PluginConsentGateScreen } from '@/presentation/components/PluginConsentGateScreen';
import { PluginLoadStepScreen } from '@/presentation/components/PluginLoadStepScreen';
import { useActivationFlow } from '@/presentation/hooks/useActivationFlow';
import { resolveConsentText } from '@/domain/entities/EventConsent';

/**
 * Componente de apresentação puro: renderiza a tela correspondente ao passo
 * atual do fluxo de ativação (PIN -> config -> lead fictício, HUB-147/
 * HUB-235/HUB-236). Toda a orquestração (transições de estado, chamadas aos
 * use cases) vive em `useActivationFlow`.
 *
 * `OnlineStatusIndicator` e `EndSessionGesture` são renderizados aqui, uma
 * única vez, independente do passo atual — a posição de ambos é `fixed`
 * (Design > Indicador de Status; LOCAL-003 > gesto de encerramento), então
 * não importa onde na árvore são montados; renderizá-los fora do `switch`
 * de passos evita uma segunda instância (a Fatia 1 tinha uma cópia extra do
 * indicador dentro de `ConfigLoadingScreen`, removida nesta fatia).
 */
export function ActivationFlow(): ReactElement {
  const {
    step,
    pinAuth,
    eventConfig,
    leadCapture,
    pluginRegistry,
    pluginLoad,
    indicatorState,
    pendingCount,
    queueAlertVisible,
    queueFullVisible,
    dismissQueueAlert,
    closeQueueFullModal,
    handlePinSubmit,
    handleRetryConfig,
    handleBackFromExpired,
    handleEndSession,
    handleLeadSubmit,
    handleCaptureAnother,
    handleRetryPluginRegistry,
    handlePluginConsentApprove,
    handlePluginConsentDecline,
    handlePluginLeadCapture,
    handlePluginComplete,
  } = useActivationFlow();

  let stepElement: ReactElement;

  if (step.kind === 'pin-entry') {
    stepElement = (
      <PinEntryScreen
        onSubmit={handlePinSubmit}
        isLoading={pinAuth.status === 'loading'}
        errorMessage={pinAuth.errorMessage}
      />
    );
  } else if (step.kind === 'loading-config') {
    stepElement = (
      <ConfigLoadingScreen errorMessage={eventConfig.errorMessage} onRetry={handleRetryConfig} />
    );
  } else if (step.kind === 'session-expired') {
    stepElement = (
      <SessionExpiredScreen expiredAt={step.expiredAt} onBack={handleBackFromExpired} />
    );
  } else if (step.kind === 'lead-capture') {
    stepElement = (
      <FakeLeadCaptureForm
        onSubmit={handleLeadSubmit}
        isLoading={leadCapture.status === 'loading'}
        errorMessage={leadCapture.errorMessage}
        consentText={resolveConsentText(step.consent)}
      />
    );
  } else if (step.kind === 'success') {
    stepElement = (
      <LeadCaptureSuccess leadName={step.leadName} onCaptureAnother={handleCaptureAnother} />
    );
  } else if (step.kind === 'resolving-plugin') {
    stepElement = (
      <PluginRegistryResolvingScreen state={pluginRegistry.state} onRetry={handleRetryPluginRegistry} />
    );
  } else if (step.kind === 'plugin-not-configured') {
    stepElement = <PluginNotConfiguredScreen />;
  } else if (step.kind === 'plugin-consent-gate') {
    stepElement = (
      <PluginConsentGateScreen
        consentText={resolveConsentText(step.consent)}
        onApprove={handlePluginConsentApprove}
        onDecline={handlePluginConsentDecline}
      />
    );
  } else {
    stepElement = (
      <PluginLoadStepScreen
        state={pluginLoad.state}
        config={step.activationConfig}
        onLeadCapture={handlePluginLeadCapture}
        onComplete={handlePluginComplete}
        onRenderError={pluginLoad.reportRenderFailure}
        onRetry={pluginLoad.retryManually}
      />
    );
  }

  return (
    <>
      <OnlineStatusIndicator state={indicatorState} pendingCount={pendingCount} />
      <EndSessionGesture onTrigger={handleEndSession} />
      {queueAlertVisible && <QueueAlertToast onDismiss={dismissQueueAlert} />}
      {queueFullVisible && <QueueFullModal onClose={closeQueueFullModal} />}
      {stepElement}
    </>
  );
}
